import { toast } from 'sonner';

import { useUserContext } from '../../../Contexts/User';
import { Icon } from '../icon';
import { Badge } from '../shadcn/badge';
import { DocFact } from '@/api/types/backend-dto-types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { Relevance } from '@/types';

export function DocViewerFact({ fact }: { fact: DocFact }) {
  const { user } = useUserContext();

  const RelevanceIndicator = ({ relevance }: { relevance: Relevance }) => {
    const getRelevanceInfo = () => {
      switch (relevance) {
        case Relevance.Relevant:
          return { text: 'Relevant', color: 'text-green-800', icon: <Icon className="lucide--circle-check" /> };
        case Relevance.MaybeRelevant:
          return {
            text: 'Maybe Relevant',
            color: 'text-orange-400',
            icon: <Icon className="lucide--circle-help" />,
          };
        case Relevance.NotRelevant:
          return { text: 'Not Relevant', color: 'text-red-800', icon: <Icon className="lucide--circle-x" /> };
        default:
          return { text: 'Not Relevant', color: 'text-red-800', icon: <Icon className="lucide--circle-x" /> };
      }
    };

    const { text, color, icon } = getRelevanceInfo();

    return (
      <p className={`flex max-w-32 items-center justify-end gap-2 text-xs ${color}`}>
        {icon}
        <span>{text}</span>
      </p>
    );
  };
  return (
    <div className="flex w-full items-center justify-between font-normal">
      <div className={`w-full p-2 px-2 pl-2`}>
        <div className="flex w-full justify-between">
          <div className={`flex items-center gap-2 text-xs font-semibold`}>
            {user?.organisation?.isLabellingOrg && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(fact.id);
                  toast.success('Fact ID copied to clipboard');
                }}
              >
                <Icon className="lucide--copy text-gray-500 duration-150 hover:text-gray-900" />
              </button>
            )}
            <Icon className="lucide--calendar" />
            {fact.factDateText ? fact.factDateText : '(Date Unknown)'}

            {fact.dateIsAmbiguous && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Icon className="lucide--circle-alert text-yellow-500" />
                </TooltipTrigger>
                <TooltipContent side="top" align="start">
                  {fact.dateIsAmbiguousText || 'Date format is ambiguous, please check'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-2">
            {fact.isDisputed && (
              <Badge className="bg-red-100">
                <span className="iconify lucide--triangle-alert text-red-500" />
                <span className="text-red-700">Disputed</span>
              </Badge>
            )}
            {fact.relevance && <RelevanceIndicator relevance={fact.relevance} />}
          </div>
        </div>

        <div className="flex justify-between gap-1">
          <div className="line-clamp-1 pt-1 text-xs text-gray-900">{fact.description}</div>
        </div>
      </div>
    </div>
  );
}
